import { OperationVariables } from '@apollo/client';
import { Button, Divider, Link, Stack, Text } from '@chakra-ui/react';
import { FileUploadBox } from '@gamma/ui/form-fields';
import { Alert } from '@gamma/ui/overlay';
import { Dispatch, SetStateAction, useEffect, useState } from 'react';
import { FieldValues, UseFormReturn } from 'react-hook-form';
import { i18n } from '../../../../../../../../shared/localization';

import { Panel, PanelHeading } from '@gamma/ui/layout';
import { safeParse } from 'secure-json-parse';
import { storeIdentityProviderProps } from '../IdpConfigurationModal';

interface ConfigureInvestigatorProps {
  useFormProps: UseFormReturn<FieldValues, unknown>;
  xmlFile: File[];
  showAlert: string;
  fileError: string;
  setFileError: Dispatch<SetStateAction<string>>;
  setXmlFile: Dispatch<SetStateAction<File[]>>;
  resetModalForm: () => void;
  storeIdentityProviderData: (input: storeIdentityProviderProps) => void;
  data: OperationVariables | undefined;
  isUpdating: boolean;
  useIDPUploadXMLHandlerProps: {
    uploadFile: (files: File[]) => Promise<File[]>;
    autoFill: (data: Record<string, string>) => void;
  };
  parsedData: Record<string, string> | undefined;
  setParsedData: Dispatch<SetStateAction<Record<string, string> | undefined>>;
  setUpdateIDPConfig: Dispatch<SetStateAction<boolean>>;
}

export const ConfigureInvestigator = ({
  xmlFile,
  showAlert,
  fileError,
  setFileError,
  setXmlFile,
  resetModalForm,
  data,
  isUpdating,
  useIDPUploadXMLHandlerProps,
  parsedData,
  setParsedData,
  setUpdateIDPConfig,
}: ConfigureInvestigatorProps) => {
  const { idpConfigurationModal, form } = i18n.pages.access;
  const describeData = data;
  const { uploadFile, autoFill } = useIDPUploadXMLHandlerProps;
  const [showValidationMore, setShowValidationMore] = useState<boolean>(false);

  useEffect(() => {
    if (describeData?.provider_details) {
      const xmlString = safeParse(describeData.provider_details);
      autoFill(xmlString);
      setXmlFile([]);
      setFileError('');
      setUpdateIDPConfig(false);
    }
  }, [describeData]);

  const updateInvalidState = (e: File[], error: string) => {
    setFileError(error);
    setXmlFile(e);
    setParsedData({});
  };

  const onUploadChange = async (e: File[]) => {
    resetModalForm();
    if (e.length) {
      if (e[0].type !== 'text/xml') {
        updateInvalidState(e, form.fileUpload.invalidType);
      } else if (e[0].size > 32 * 1024) {
        updateInvalidState(e, form.fileUpload.invalidSize);
      } else if (e[0].type === 'text/xml' && e[0].size <= 32 * 1024) {
        setFileError('');
        const file = await uploadFile(e);
        setXmlFile(file);
        if (isUpdating) {
          setUpdateIDPConfig(true);
        }
      }
    } else {
      resetModalForm();
      setUpdateIDPConfig(false);
    }
  };

  return (
    <Stack spacing={6}>
      <Stack spacing={3}>
        {showAlert && <Alert status="error" title={showAlert} />}
        <FileUploadBox
          name="upload_file"
          label={form.uploadBox}
          uploadText={form.uploadBox}
          isLabelHidden
          accept="text/xml"
          onChange={onUploadChange}
          value={xmlFile}
          fieldError={fileError}
        />
        {parsedData?.signon_url && (
          <>
            <Divider />
            <Panel layerStyle="second">
              <PanelHeading>{form.ssoUrl.label}</PanelHeading>
              <Text>{parsedData?.signon_url}</Text>
            </Panel>
            <Panel layerStyle="second">
              <PanelHeading>{form.entityId.label}</PanelHeading>
              <Text>{parsedData?.entity_id}</Text>
            </Panel>
            <Panel layerStyle="second">
              <PanelHeading>{form.certificate.label}</PanelHeading>
              <Text noOfLines={showValidationMore ? undefined : 3}>
                {parsedData?.certificate}
              </Text>
              {parsedData?.certificate.length > 128 && (
                <Button
                  variant="link"
                  textDecor={'none'}
                  size="sm"
                  colorScheme="blue"
                  mt={1}
                  onClick={() => setShowValidationMore(!showValidationMore)}
                >
                  {showValidationMore
                    ? form.showToggle.showLess
                    : form.showToggle.showMore}
                </Button>
              )}
            </Panel>
          </>
        )}
      </Stack>
      <Stack>
        <Text>{idpConfigurationModal.configure.help}</Text>
        <Link
          isExternal
          color="blue.400"
          href="/docs/settings/users/saml-sso-users.html"
          width="fit-content"
        >
          {idpConfigurationModal.configure.helpTextSSO}
        </Link>
      </Stack>
    </Stack>
  );
};
